import type { Language } from '../types/models';

const messages = {
  en: {
    appName: 'BajetBN',
    personalSpace: 'Personal Space',
    accounts: 'Accounts',
    spaces: 'Spaces',
  },
  ms: {
    appName: 'BajetBN',
    personalSpace: 'Ruang Peribadi',
    accounts: 'Akaun',
    spaces: 'Ruang',
  },
} as const;

export type MessageKey = keyof typeof messages.en;

export function translate(language: Language, key: MessageKey): string {
  return messages[language]?.[key] ?? messages.en[key];
}

export function localeForLanguage(language: Language): string {
  return language === 'ms' ? 'ms-BN' : 'en-BN';
}

const malayPhrases: Record<string, string> = {
  'Built for Brunei': 'Dibina untuk Brunei',
  'Your money, your goals, one clear place.': 'Wang anda, matlamat anda, semuanya jelas di satu tempat.',
  'Track spending, manage household and trip funds, grow your savings, and keep Business finances organised—all with BajetBN.': 'Jejaki perbelanjaan, urus dana isi rumah dan perjalanan, tingkatkan simpanan dan pastikan kewangan perniagaan teratur—semuanya dengan BajetBN.',
  'Create your account': 'Buat akaun anda',
  'Start with your Personal Space': 'Mulakan dengan Ruang Peribadi anda',
  'We will help you set up your first private Space.': 'Kami akan membantu menyediakan Ruang Peribadi pertama anda.',
  'Register with Google': 'Daftar dengan Google',
  'A new registration after account deletion starts fresh. Previous Spaces, balances and memberships are not restored.': 'Pendaftaran baharu selepas akaun dipadam bermula dari awal. Ruang, baki dan keahlian terdahulu tidak dipulihkan.',
  'Already registered?': 'Sudah berdaftar?',
  'A servant will not move on the Day of Judgement until questioned... about his wealth: how it was earned and how it was spent.': 'Tidak akan berganjak kaki seorang hamba pada hari kiamat sehingga dia ditanya... tentang hartanya; dari mana diperolehnya dan ke mana dibelanjakannya.',
  "Jami' al-Tirmidhi (No. 2417)": "Riwayat Jami' al-Tirmidhi (No. 2417)",
  'Step 1 of 2': 'Langkah 1 daripada 2',
  'Continue': 'Teruskan',
  'Step 2 of 2': 'Langkah 2 daripada 2',
  'Choose your theme': 'Pilih tema anda',
  'Preview a look for BajetBN. You can change your theme anytime in Settings.': 'Pratonton rupa BajetBN. Anda boleh menukar tema pada bila-bila masa dalam Tetapan.',
  'Back': 'Kembali',
  'Skip for now': 'Langkau buat masa ini',
  'Use this theme & continue': 'Gunakan tema ini dan teruskan',
  'You can change your theme anytime in Settings.': 'Anda boleh menukar tema pada bila-bila masa dalam Tetapan.',
  'Default currency: BND': 'Mata wang utama: BND',
  'English & Malay-ready': 'Bahasa Inggeris dan Melayu',
  'Private by design': 'Direka untuk privasi',
  'Welcome back': 'Selamat kembali',
  'Sign in to BajetBN': 'Log masuk ke BajetBN',
  'Open your personal and shared money Spaces.': 'Buka Ruang wang peribadi dan kongsi anda.',
  'Continue with Google': 'Teruskan dengan Google',
  'or use email': 'atau guna e-mel',
  'Email': 'E-mel',
  'Password': 'Kata laluan',
  'Sign in': 'Log masuk',
  'Signing in…': 'Sedang log masuk…',
  'New to BajetBN?': 'Baharu di BajetBN?',
  'Create an account': 'Buat akaun',
  'Create your BajetBN account': 'Buat akaun BajetBN anda',
  'Already have an account?': 'Sudah ada akaun?',
  'Full name': 'Nama penuh',
  'Your full name': 'Nama penuh anda',
  'Confirm password': 'Sahkan kata laluan',
  'Create account': 'Buat akaun',
  'Creating account…': 'Sedang membuat akaun…',
  'Verify your email': 'Sahkan e-mel anda',
  'Check your inbox and use the verification link before signing in.': 'Semak peti masuk dan tekan pautan pengesahan sebelum log masuk.',
  'Back to sign in': 'Kembali ke log masuk',
  'Step 1 of 1': 'Langkah 1 daripada 1',
  'Set up your BajetBN home': 'Sediakan BajetBN anda',
  'We will create your private Personal Space. You can add household, business, trip, goal, or other Spaces later.': 'Kami akan membuat Ruang Peribadi anda. Anda boleh menambah Ruang keluarga, perniagaan, perjalanan, matlamat atau lain-lain kemudian.',
  'Language': 'Bahasa',
  'Currency': 'Mata wang',
  'Timezone': 'Zon masa',
  'Brunei time': 'Waktu Brunei',
  'Personal Space': 'Ruang Peribadi',
  'Private': 'Peribadi',
  'Owner only': 'Pemilik sahaja',
  'We will create this for you': 'Kami akan membuatnya untuk anda',
  'Creating your Space…': 'Sedang membuat Ruang anda…',
  'Finish setup': 'Selesai persediaan',
  'Step 1 of 3': 'Langkah 1 daripada 3',
  'Step 2 of 3': 'Langkah 2 daripada 3',
  'Step 3 of 3': 'Langkah 3 daripada 3',
  'Tell us about you': 'Beritahu kami tentang anda',
  'What do you want to organise first?': 'Apakah yang anda mahu urus dahulu?',
  'Your first setup plan': 'Pelan persediaan pertama anda',
  'Personal money': 'Wang peribadi',
  'Household & family': 'Isi rumah & keluarga',
  'Business': 'Perniagaan',
  'Trip / travel': 'Perjalanan',
  'Review my setup': 'Semak persediaan saya',
  'Your focus': 'Fokus anda',
  'Created automatically': 'Dibuat secara automatik',
  'Ready': 'Sedia',
  'Next': 'Seterusnya',
  'Suggested': 'Dicadangkan',
  'Overview': 'Ringkasan',
  'Spaces': 'Ruang',
  'Accounts': 'Akaun',
  'Money activity': 'Aktiviti wang',
  'Budgets': 'Bajet',
  'Goals': 'Matlamat',
  'Bills & instalments': 'Bil & ansuran',
  'Sharing': 'Perkongsian',
  'Calendar': 'Kalendar',
  'Search': 'Cari',
  'Money reports': 'Laporan wang',
  'Settings': 'Tetapan',
  'Sign out': 'Log keluar',
  'BajetBN user': 'Pengguna BajetBN',
  'Search BajetBN': 'Cari dalam BajetBN',
  'Open menu': 'Buka menu',
  'Close menu': 'Tutup menu',
  'Close navigation': 'Tutup navigasi',
  'Toggle sidebar': 'Buka atau tutup menu sisi',
  'Preferences': 'Pilihan',
  'Your settings': 'Tetapan anda',
  'Keep BajetBN easy to read and suited to how you manage money.': 'Jadikan BajetBN mudah dibaca dan sesuai dengan cara anda mengurus wang.',
  'Profile': 'Profil',
  'Name': 'Nama',
  'Language and appearance': 'Bahasa dan paparan',
  'Appearance': 'Paparan',
  'Dark': 'Gelap',
  'Light': 'Cerah',
  'Use device setting': 'Ikut tetapan peranti',
  'Text size': 'Saiz tulisan',
  'Normal': 'Biasa',
  'Large': 'Besar',
  'Money and time': 'Wang dan masa',
  'BND — Brunei Dollar': 'BND — Dolar Brunei',
  'Asia/Brunei (UTC+8)': 'Asia/Brunei (UTC+8)',
  'BajetBN uses BND and Brunei time so dates and amounts stay clear.': 'BajetBN menggunakan BND dan waktu Brunei supaya tarikh dan jumlah mudah difahami.',
  'Reminders': 'Peringatan',
  'Show reminders inside BajetBN': 'Tunjukkan peringatan dalam BajetBN',
  'BajetBN can prepare reminders even when the app is closed.': 'BajetBN boleh menyediakan peringatan walaupun aplikasi ditutup.',
  'Prepare reminders while BajetBN is closed': 'Sediakan peringatan semasa BajetBN ditutup',
  'Scheduled checks create reminders without needing the app to stay open.': 'Semakan berjadual menyediakan peringatan tanpa aplikasi perlu sentiasa dibuka.',
  'Remind me about goal dates': 'Ingatkan saya tentang tarikh matlamat',
  'Device notifications': 'Pemberitahuan peranti',
  'Turn on for this device': 'Hidupkan untuk peranti ini',
  'Turn off on my devices': 'Matikan pada peranti saya',
  'Check reminders now': 'Semak peringatan sekarang',
  'Prepared in background': 'Disediakan di latar belakang',
  'Background': 'Latar belakang',
  'Remind me before a bill is due': 'Ingatkan saya sebelum bil perlu dibayar',
  'Tell me when a bill is late': 'Beritahu saya apabila bil lewat',
  'Tell me about shared payments': 'Beritahu saya tentang bayaran kongsi',
  'Show WhatsApp reminder buttons': 'Tunjukkan butang peringatan WhatsApp',
  'Remind me this many days before': 'Ingatkan saya beberapa hari sebelum',
  'days': 'hari',
  'Save settings': 'Simpan tetapan',
  'Saving…': 'Sedang menyimpan…',
  'Settings saved.': 'Tetapan disimpan.',
  'Account controls': 'Kawalan akaun',
  'Sign out of this device': 'Log keluar daripada peranti ini',
  'Download my data — coming later': 'Muat turun data saya — akan datang',
  'Delete my account — coming later': 'Padam akaun saya — akan datang',
  'Your privacy and data tools will stay available even without a paid plan.': 'Alat privasi dan data anda akan tetap tersedia walaupun tanpa pelan berbayar.',
  'Add account': 'Tambah akaun',
  'Add Space': 'Tambah Ruang',
  'Add transaction': 'Tambah aktiviti wang',
  'Add budget': 'Tambah bajet',
  'Add goal': 'Tambah matlamat',
  'Add bill or instalment': 'Tambah bil atau ansuran',
  'Add member': 'Tambah ahli',
  'Invite member': 'Jemput ahli',
  'How invitations work': 'Cara jemputan berfungsi',
  'The person joins using this email. Send the secure link directly through WhatsApp or copy it.': 'Orang itu menyertai menggunakan e-mel ini. Hantar pautan selamat terus melalui WhatsApp atau salin pautan.',
  'Email address': 'Alamat e-mel',
  'They must sign in to BajetBN using this email.': 'Mereka mesti log masuk ke BajetBN menggunakan e-mel ini.',
  'WhatsApp number': 'Nombor WhatsApp',
  'Optional': 'Pilihan',
  'Leave blank to choose a WhatsApp contact later.': 'Biarkan kosong untuk memilih kenalan WhatsApp kemudian.',
  'What can this person do?': 'Apakah yang boleh dilakukan oleh orang ini?',
  'Manager': 'Pengurus',
  'Add money records': 'Tambah rekod wang',
  'Record payments': 'Rekod bayaran',
  'Manage members, Space settings, and shared records.': 'Urus ahli, tetapan Ruang dan rekod kongsi.',
  'Add and update shared money records.': 'Tambah dan kemas kini rekod wang kongsi.',
  'Record payments assigned to them.': 'Rekod bayaran yang diberikan kepada mereka.',
  'View shared information without changing anything.': 'Lihat maklumat kongsi tanpa mengubah apa-apa.',
  'Shared account access': 'Akses akaun kongsi',
  'Leave these off unless this person needs information from shared Accounts.': 'Biarkan pilihan ini ditutup kecuali orang ini memerlukan maklumat daripada Akaun kongsi.',
  'Use shared accounts': 'Guna akaun kongsi',
  'Choose a shared Account when recording money.': 'Pilih Akaun kongsi semasa merekod wang.',
  'See account balances': 'Lihat baki akaun',
  'See how much money is available.': 'Lihat jumlah wang yang tersedia.',
  'See money going in and out.': 'Lihat wang masuk dan keluar.',
  'Send invite on WhatsApp': 'Buat dan hantar dengan WhatsApp',
  'Copy invite link': 'Buat dan salin pautan',
  'WhatsApp will open with a ready message. You still choose the contact and press Send.': 'WhatsApp akan dibuka dengan mesej siap. Anda masih memilih kenalan dan menekan Hantar.',
  'Save': 'Simpan',
  'Cancel': 'Batal',
  'Close': 'Tutup',
  'Open': 'Buka',
  'Edit': 'Ubah',
  'Delete': 'Padam',
  'Undo': 'Batalkan',
  'Undo payment': 'Batalkan bayaran',
  'Confirm': 'Sahkan',
  'Confirm payment': 'Sahkan bayaran',
  'Decline payment': 'Tolak bayaran',
  'Finish payment': 'Selesaikan bayaran',
  'Finish old payment': 'Selesaikan bayaran lama',
  'Amount': 'Jumlah',
  'Amount paid': 'Jumlah dibayar',
  'Amount left': 'Baki perlu dibayar',
  'Still to pay': 'Masih perlu dibayar',
  'Paid': 'Sudah dibayar',
  'Paid part of it': 'Dibayar sebahagian',
  'Late': 'Lewat',
  'Due today': 'Perlu dibayar hari ini',
  'Coming soon': 'Akan datang',
  'Later': 'Kemudian',
  'All': 'Semua',
  'Bills': 'Bil',
  'Instalments': 'Ansuran',
  'Income': 'Wang masuk',
  'Expense': 'Wang keluar',
  'Transfer': 'Pindahan',
  'Money in': 'Wang masuk',
  'Money out': 'Wang keluar',
  'Money left': 'Baki wang',
  'This month': 'Bulan ini',
  'Previous month': 'Bulan sebelumnya',
  'Today': 'Hari ini',
  'Selected date': 'Tarikh dipilih',
  'Nothing planned': 'Tiada apa dirancang',
  'Nothing here.': 'Tiada apa di sini.',
  'No reminder history yet': 'Belum ada sejarah peringatan',
  'Mark as reminded': 'Tanda sudah diingatkan',
  'Marked as reminded': 'Ditanda sudah diingatkan',
  'WhatsApp reminder opened': 'Peringatan WhatsApp dibuka',
  'Reminder history': 'Sejarah peringatan',
  'Next month': 'Bulan seterusnya',
  'Search everything': 'Cari semua',
  'Search accounts, money activity, bills, goals and Spaces.': 'Cari akaun, aktiviti wang, bil, matlamat dan Ruang.',
  'No results found': 'Tiada hasil ditemui',
  'Try another word or remove some filters.': 'Cuba perkataan lain atau buang beberapa penapis.',
  'Filters': 'Penapis',
  'Clear filters': 'Kosongkan penapis',
  'Space': 'Ruang',
  'Account': 'Akaun',
  'Category': 'Kategori',
  'Status': 'Status',
  'Date': 'Tarikh',
  'From': 'Dari',
  'To': 'Hingga',
  'Loading…': 'Sedang dimuatkan…',
  'Try again': 'Cuba lagi',
  'You do not have access to this page.': 'Anda tidak mempunyai akses ke halaman ini.',
  'Something went wrong. Please try again.': 'Ada masalah. Sila cuba lagi.',
  'You are offline. Some information may be older.': 'Anda sedang luar talian. Sesetengah maklumat mungkin lama.',
  'Back online.': 'Kembali dalam talian.',
  'Page not found': 'Halaman tidak ditemui',
  'Go to overview': 'Pergi ke ringkasan',
  'Life, connected by money': 'Hidup, dihubungkan oleh wang',
  'Add your bank, cash, e-wallet, or credit card accounts. Choose one when recording money in or out.': 'Tambah akaun bank, tunai, e-dompet atau kad kredit. Pilih satu apabila merekod wang masuk atau keluar.',
  'Add your first account': 'Tambah akaun pertama anda',
  'Credit card': 'Kad kredit',
  'Edit account': 'Ubah akaun',
  'Money sources': 'Sumber wang',
  'Save changes': 'Simpan perubahan',
  'Start with BIBD, Baiduri, Cash, an e-wallet, or a credit card.': 'Mulakan dengan BIBD, Baiduri, Tunai, e-dompet atau kad kredit.',
  'Passwords do not match.': 'Kata laluan tidak sama.',
  'Use at least 8 characters.': 'Gunakan sekurang-kurangnya 8 aksara.',
  'Not verified yet. Open the email link, then check again.': 'Belum disahkan. Buka pautan dalam e-mel, kemudian semak lagi.',
  'Verification email sent again.': 'E-mel pengesahan dihantar semula.',
  'All spending categories': 'Semua kategori perbelanjaan',
  'Chosen dates': 'Tarikh dipilih',
  'Edit budget': 'Ubah bajet',
  'End date must be on or after the start date.': 'Tarikh akhir mesti sama atau selepas tarikh mula.',
  'Enter a budget greater than BND 0.00.': 'Masukkan bajet lebih daripada BND 0.00.',
  'Enter a name and choose a Space.': 'Masukkan nama dan pilih Ruang.',
  'Monthly groceries': 'Barang dapur bulanan',
  'Set how much you plan to spend. Saved expenses update the matching budget automatically.': 'Tetapkan jumlah yang anda mahu belanjakan. Wang keluar yang disimpan akan mengemas kini bajet yang sesuai secara automatik.',
  'Calendar & reminders': 'Kalendar & peringatan',
  'See what is late, due today, or coming soon.': 'Lihat perkara yang lewat, perlu dibayar hari ini atau akan datang.',
  'Plan ahead': 'Rancang awal',
  'Bill payment': 'Bayaran bil',
  'Instalment payment': 'Bayaran ansuran',
  'Shared bill': 'Bil kongsi',
  'Amount still needed for this goal': 'Jumlah yang masih diperlukan untuk matlamat ini',
  'Your share of a shared bill': 'Bahagian anda untuk bil kongsi',
  'Add a WhatsApp number for this Space on the Sharing page first.': 'Tambah nombor WhatsApp untuk Ruang ini di halaman Perkongsian dahulu.',
  'Add a WhatsApp number in Sharing first': 'Tambah nombor WhatsApp di Perkongsian dahulu',
  'Open WhatsApp with a ready message': 'Buka WhatsApp dengan mesej siap',
  'WhatsApp opened with a ready reminder message.': 'WhatsApp dibuka dengan mesej peringatan siap.',
  'Use Mark as reminded or WhatsApp and it will appear here.': 'Gunakan Tanda sudah diingatkan atau WhatsApp dan rekod akan muncul di sini.',
  'There are no bills, instalments, shared bills, or goal dates on this day.': 'Tiada bil, ansuran, bil kongsi atau tarikh matlamat pada hari ini.',
  'Just now': 'Baru sahaja',
  'Share with others': 'Kongsi dengan orang lain',
  'Shared money': 'Wang kongsi',
  'Invite family, friends, or team members to a shared Space.': 'Jemput keluarga, kawan atau ahli pasukan ke Ruang kongsi.',
  'Manage members, split bills, check payments, view proof, and send WhatsApp notices.': 'Urus ahli, bahagikan bil, semak bayaran, lihat bukti dan hantar notis WhatsApp.',
  'Create invite': 'Buat jemputan',
  'Creating invite…': 'Sedang membuat jemputan…',
  'Change member access': 'Ubah akses ahli',
  'Save access': 'Simpan akses',
  'Save sharing settings': 'Simpan tetapan perkongsian',
  'Give a bill share': 'Beri bahagian bil',
  'Give bill share': 'Beri bahagian bil',
  'Add payment': 'Tambah bayaran',
  'Payment method': 'Cara bayaran',
  'Bank transfer': 'Pindahan bank',
  'Cash': 'Tunai',
  'Debit card': 'Kad debit',
  'E-wallet': 'E-dompet',
  'QR payment': 'Bayaran QR',
  'Bank counter or ATM deposit': 'Deposit kaunter bank atau ATM',
  'Cheque': 'Cek',
  'Other method': 'Cara lain',
  'Type the payment method': 'Taip cara bayaran',
  'Institution or provider': 'Institusi atau penyedia',
  'Choose a common Brunei option or type another institution. Existing custom names still work.': 'Pilih pilihan Brunei yang biasa atau taip institusi lain. Nama tersuai sedia ada masih boleh digunakan.',
  'Another method': 'Cara lain',
  'My BajetBN account': 'Akaun BajetBN saya',
  'Paid another way': 'Dibayar dengan cara lain',
  'Choose the account used for this payment.': 'Pilih akaun yang digunakan untuk bayaran ini.',
  'Enter an amount greater than BND 0.00.': 'Masukkan jumlah lebih daripada BND 0.00.',
  'The amount paid cannot be more than the amount left to pay.': 'Jumlah dibayar tidak boleh lebih daripada baki perlu dibayar.',
  'Member payments are accepted automatically.': 'Bayaran ahli diterima secara automatik.',
  'The Space owner or admin checks each payment before it is accepted.': 'Pemilik atau pentadbir Ruang menyemak setiap bayaran sebelum diterima.',
  'Owner checks': 'Pemilik menyemak',
  'Waiting for check': 'Menunggu semakan',
  'Not accepted': 'Tidak diterima',
  'Not paid': 'Belum dibayar',
  'Old payment needs finishing': 'Bayaran lama perlu diselesaikan',
  'Account updated': 'Akaun dikemas kini',
  'Account activity hidden': 'Aktiviti akaun disembunyikan',
  'Balances hidden': 'Baki disembunyikan',
  'Can add': 'Boleh tambah',
  'Can pay': 'Boleh bayar',
  'No account use': 'Tidak boleh guna akaun',
  'See account activity': 'Lihat aktiviti akaun',
  'See balances': 'Lihat baki',
  'Use accounts': 'Guna akaun',
  'View only': 'Lihat sahaja',
  'Send payment details': 'Hantar maklumat bayaran',
  'Receipt number or short message': 'Nombor resit atau mesej ringkas',
  'Join a shared Space': 'Sertai Ruang kongsi',
  'Join this Space': 'Sertai Ruang ini',
  'Sign in with the same email address that received the invite.': 'Log masuk dengan alamat e-mel yang menerima jemputan.',
  'Track bills and instalments, then record payments from the account you used.': 'Jejak bil dan ansuran, kemudian rekod bayaran daripada akaun yang digunakan.',
  'Amount due each cycle': 'Jumlah perlu dibayar setiap kali',
  'Instalment amount per cycle': 'Jumlah ansuran setiap kali',
  'Choose an account when you pay': 'Pilih akaun apabila membayar',
  'Coming up': 'Akan datang',
  'One time': 'Sekali sahaja',
  'Every 3 months': 'Setiap 3 bulan',
  'Save bill or instalment': 'Simpan bil atau ansuran',
  'Save payment': 'Simpan bayaran',
  'Edit bill or instalment': 'Ubah bil atau ansuran',
  'Enter a payment greater than BND 0.00.': 'Masukkan bayaran lebih daripada BND 0.00.',
  'The full instalment total must be the same as or more than one payment.': 'Jumlah penuh ansuran mesti sama atau lebih daripada satu bayaran.',
  'Savings goals': 'Matlamat simpanan',
  'Track money you plan to save for emergencies, school, travel, equipment, or anything else.': 'Jejak wang yang anda mahu simpan untuk kecemasan, sekolah, perjalanan, peralatan atau perkara lain.',
  'Add savings goal': 'Tambah matlamat simpanan',
  'Edit goal': 'Ubah matlamat',
  'Save goal': 'Simpan matlamat',
  'Save progress': 'Simpan kemajuan',
  'In progress': 'Sedang berjalan',
  'No deadline': 'Tiada tarikh akhir',
  'Enter a target greater than BND 0.00.': 'Masukkan sasaran lebih daripada BND 0.00.',
  'Undo this saved progress?': 'Batalkan kemajuan yang disimpan ini?',
  'See where your money came from, where it went, and what may need your attention.': 'Lihat dari mana wang datang, ke mana ia pergi dan perkara yang mungkin perlu perhatian.',
  'Report filters': 'Penapis laporan',
  'All time': 'Sepanjang masa',
  'All saved income': 'Semua wang masuk yang disimpan',
  'All saved spending': 'Semua perbelanjaan yang disimpan',
  'Same as the previous month': 'Sama seperti bulan sebelumnya',
  'No activity in this or the previous month': 'Tiada aktiviti pada bulan ini atau bulan sebelumnya',
  'Spending category': 'Kategori perbelanjaan',
  'Paid from this account': 'Dibayar daripada akaun ini',
  'Money group': 'Kumpulan wang',
  'Unknown Space': 'Ruang tidak diketahui',
  'Unknown account': 'Akaun tidak diketahui',
  'No spending found for these filters.': 'Tiada perbelanjaan ditemui untuk penapis ini.',
  'No account spending found for these filters.': 'Tiada perbelanjaan akaun ditemui untuk penapis ini.',
  'No Space spending found for these filters.': 'Tiada perbelanjaan Ruang ditemui untuk penapis ini.',
  'There is no money activity for these filters yet. Add income or an expense to see a report.': 'Belum ada aktiviti wang untuk penapis ini. Tambah wang masuk atau keluar untuk melihat laporan.',
  'Your shown budgets are within the planned amounts.': 'Bajet yang ditunjukkan masih dalam jumlah yang dirancang.',
  'Find anything': 'Cari apa sahaja',
  'Search your accounts, money activity, bills, goals, and Spaces.': 'Cari akaun, aktiviti wang, bil, matlamat dan Ruang anda.',
  'Try: Wi-Fi, salary, school, BIBD…': 'Cuba: Wi-Fi, gaji, sekolah, BIBD…',
  'No matches found': 'Tiada padanan ditemui',
  'Try a shorter word or clear some filters.': 'Cuba perkataan lebih pendek atau kosongkan beberapa penapis.',
  'Nothing to search yet': 'Belum ada apa untuk dicari',
  'Add accounts, money activity, bills, or goals and they will appear here.': 'Tambah akaun, aktiviti wang, bil atau matlamat dan ia akan muncul di sini.',
  'Bill or instalment': 'Bil atau ansuran',
  'Savings goal': 'Matlamat simpanan',
  'Account transfer': 'Pindahan akaun',
  'Cash account': 'Akaun tunai',
  'Money account': 'Akaun wang',
  'No next date': 'Tiada tarikh seterusnya',
  'No target date': 'Tiada tarikh sasaran',
  'Money groups': 'Kumpulan wang',
  'Use Spaces to separate personal, household, trip, or business money.': 'Gunakan Ruang untuk memisahkan wang peribadi, keluarga, perjalanan atau perniagaan.',
  'Your Personal Space will appear after onboarding.': 'Ruang Peribadi anda akan muncul selepas persediaan.',
  'Your private money area.': 'Kawasan wang peribadi anda.',
  'No Spaces added yet': 'Belum ada Ruang ditambah',
  'Edit Space': 'Ubah Ruang',
  'Optional details': 'Maklumat tambahan',
  'Record money in, money out, and money moved between accounts.': 'Rekod wang masuk, wang keluar dan wang yang dipindahkan antara akaun.',
  'Add money activity': 'Tambah aktiviti wang',
  'Save money activity': 'Simpan aktiviti wang',
  'Money activity details': 'Maklumat aktiviti wang',
  'Money activity type': 'Jenis aktiviti wang',
  'Money records': 'Rekod wang',
  'Money comes from': 'Wang datang dari',
  'Money goes into': 'Wang masuk ke',
  'Money from': 'Wang dari',
  'Move money': 'Pindah wang',
  'Move from account': 'Pindah dari akaun',
  'Paid to': 'Dibayar kepada',
  'Source or customer': 'Sumber atau pelanggan',
  'Shop or person paid': 'Kedai atau orang yang dibayar',
  'Search category, Space, Account, payee…': 'Cari kategori, Ruang, Akaun atau penerima…',
  'Choose a Space and Account.': 'Pilih Ruang dan Akaun.',
  'Choose a category.': 'Pilih kategori.',
  'Choose a destination Account.': 'Pilih Akaun tujuan.',
  'No matching money activity': 'Tiada aktiviti wang yang sepadan',
  'Change the filters or add money in, money out, or a money move.': 'Ubah penapis atau tambah wang masuk, wang keluar atau pindahan wang.',
  'Edit categories': 'Ubah kategori',
  'Create custom category': 'Buat kategori sendiri',
  'Edit custom category': 'Ubah kategori sendiri',
  'Category name': 'Nama kategori',
  'Category name is required.': 'Nama kategori diperlukan.',
  'Save category': 'Simpan kategori',
  'No custom categories': 'Tiada kategori sendiri',
  'Ready-made categories are available. Add your own only when you need a different name.': 'Kategori siap tersedia. Tambah kategori sendiri hanya apabila anda perlukan nama lain.',
  'Payment proof': 'Bukti bayaran',
  'Payment submitted': 'Bayaran dihantar',
  'Record number': 'Nombor rekod',
  'Unknown Account': 'Akaun tidak diketahui',
  'Undoing record': 'Rekod pembatalan',
  'Undone by': 'Dibatalkan oleh',
  'Undone from money activity details': 'Dibatalkan daripada maklumat aktiviti wang',
  'See your accounts, spending plans, savings goals, bills, and instalments in one place.': 'Lihat akaun, pelan perbelanjaan, matlamat simpanan, bil dan ansuran di satu tempat.',
  'Choose the name shown in BajetBN.': 'Pilih nama yang ditunjukkan dalam BajetBN.',
  'Changes are shown straight away. Save when you are happy.': 'Perubahan ditunjukkan terus. Simpan apabila anda berpuas hati.',
  'Current view': 'Paparan semasa',
  'Choose what BajetBN should bring to your attention.': 'Pilih perkara yang BajetBN patut ingatkan kepada anda.',
  'See helpful messages in the app.': 'Lihat mesej berguna dalam aplikasi.',
  'Show bills that need attention soon.': 'Tunjukkan bil yang perlu perhatian tidak lama lagi.',
  'Keep late bills easy to find.': 'Pastikan bil lewat mudah ditemui.',
  'Show updates when a member submits or confirms a payment.': 'Tunjukkan kemas kini apabila ahli menghantar atau mengesahkan bayaran.',
  'Open WhatsApp with a ready message. You still press Send yourself.': 'Buka WhatsApp dengan mesej siap. Anda masih perlu menekan Hantar sendiri.',
  'This page could not open': 'Halaman ini tidak dapat dibuka',
  'Your saved money information has not been changed. Reload the page and try again.': 'Maklumat wang yang disimpan tidak berubah. Muat semula halaman dan cuba lagi.',
  'Reload page': 'Muat semula halaman',
  'Go to Overview': 'Pergi ke Ringkasan',
  'My data': 'Data saya',
  'Check your account totals or save a private copy of your BajetBN information.': 'Semak jumlah akaun atau simpan salinan peribadi maklumat BajetBN anda.',
  'Check account totals': 'Semak jumlah akaun',
  'Compare each shown account total with its saved account records. This check does not change anything.': 'Bandingkan jumlah setiap akaun dengan rekod akaun yang disimpan. Semakan ini tidak mengubah apa-apa.',
  'Check my totals': 'Semak jumlah saya',
  'Checking…': 'Sedang menyemak…',
  'Save a copy of my data': 'Simpan salinan data saya',
  'Download a JSON file containing your profile, accounts, money activity, bills, goals and reminders.': 'Muat turun fail JSON yang mengandungi profil, akaun, aktiviti wang, bil, matlamat dan peringatan anda.',
  'Download my data': 'Muat turun data saya',
  'Preparing…': 'Sedang menyediakan…',
  'Everything matches': 'Semuanya sepadan',
  'Some totals do not match': 'Beberapa jumlah tidak sepadan',
  'Total matches': 'Jumlah sepadan',
  'Needs attention': 'Perlu perhatian',
  'Shown:': 'Ditunjukkan:',
  'Records:': 'Rekod:',
  'Difference:': 'Perbezaan:',
  'Your account totals match your saved account records.': 'Jumlah akaun anda sepadan dengan rekod akaun yang disimpan.',
  'One or more account totals need attention. Do not change old records. Keep this result for support.': 'Satu atau lebih jumlah akaun perlu perhatian. Jangan ubah rekod lama. Simpan hasil ini untuk bantuan.',
  'Your BajetBN data file is ready. Keep it private because it contains your money information.': 'Fail data BajetBN anda sudah siap. Simpan secara peribadi kerana ia mengandungi maklumat wang anda.',
  'Your privacy and data tools stay available without a paid plan.': 'Alat privasi dan data anda kekal tersedia tanpa pelan berbayar.',
  'This page is getting ready. Please try again in a few minutes.': 'Halaman ini sedang disediakan. Sila cuba lagi dalam beberapa minit.',
  'BajetBN could not connect. Check your internet and try again.': 'BajetBN tidak dapat bersambung. Semak internet anda dan cuba lagi.',
  'All Spaces': 'Semua Ruang',
  'Space settings': 'Tetapan Ruang',
  'Shared Spaces': 'Ruang Kongsi',
  'Shared bills': 'Bil kongsi',
  'Shared expenses': 'Perbelanjaan kongsi',
  'Manage this Space': 'Urus Ruang ini',
  'Open a section': 'Buka bahagian',
  'Open Space': 'Buka Ruang',
  'Back to Spaces': 'Kembali ke Ruang',
  'Space not found': 'Ruang tidak ditemui',
  'This Space is not available': 'Ruang ini tidak tersedia',
  'Go back to Spaces and choose another Space.': 'Kembali ke Ruang dan pilih Ruang lain.',
  'Sharing stays inside this Space': 'Perkongsian kekal dalam Ruang ini',
  'Open Members, Shared bills or Activity above. There is no separate Sharing page.': 'Buka Ahli, Bil kongsi atau Aktiviti di atas. Tiada halaman Perkongsian berasingan.',
  'Members': 'Ahli',
  'Activity': 'Aktiviti',
  'Active people in this Space': 'Orang aktif dalam Ruang ini',
  'Accounts used here': 'Akaun digunakan di sini',
  'Items that still need attention': 'Perkara yang masih perlu perhatian',
  'Your private place for personal money.': 'Tempat peribadi untuk wang anda.',
  'Manage household money, members, and shared bills together.': 'Urus wang keluarga, ahli dan bil kongsi bersama.',
  'Keep trip spending, members, and shared payments in one place.': 'Simpan perbelanjaan perjalanan, ahli dan bayaran kongsi di satu tempat.',
  'Keep business money separate from personal money.': 'Asingkan wang perniagaan daripada wang peribadi.',
  'Your Personal Space is always kept because it is the main home for your personal money.': 'Ruang Peribadi anda sentiasa disimpan kerana ia ialah tempat utama untuk wang peribadi.',
  'Edit Space name or description': 'Ubah nama atau penerangan Ruang',
  'Only the Space owner or manager can change these settings.': 'Hanya pemilik atau pengurus Ruang boleh mengubah tetapan ini.',
  'Money health': 'Kesihatan wang',
  'Financial health check': 'Semakan kesihatan kewangan',
  'Simple indicators based on the month and filters shown above.': 'Petunjuk mudah berdasarkan bulan dan penapis yang dipaparkan di atas.',
  'Savings rate': 'Kadar simpanan',
  'Budget pressure': 'Tekanan bajet',
  'Regular payment load': 'Beban bayaran tetap',
  'Emergency fund': 'Dana kecemasan',
  'Not ready': 'Belum tersedia',
  'No budget': 'Tiada bajet',
  'Not set': 'Belum disediakan',
  'Month-to-month': 'Bulan ke bulan',
  'Spending changes': 'Perubahan perbelanjaan',
  'No change': 'Tiada perubahan',
  'Add spending in two months to see changes.': 'Tambah perbelanjaan untuk dua bulan bagi melihat perubahan.',
  'Next steps': 'Langkah seterusnya',
  'What to check next': 'Perkara untuk disemak seterusnya',
  'Business overview': 'Ringkasan perniagaan',
  'A simple business view for the selected month.': 'Paparan perniagaan mudah untuk bulan yang dipilih.',
  'Business money in': 'Wang perniagaan masuk',
  'Business money out': 'Wang perniagaan keluar',
  'Simple profit check': 'Semakan untung mudah',
  'Current cash position': 'Kedudukan tunai semasa',
  'Upcoming payments — next 30 days': 'Bayaran akan datang — 30 hari seterusnya',
  'Optional shared money': 'Wang kongsi pilihan',
  'Household fund': 'Dana keluarga',
  'Group fund': 'Dana kumpulan',
  'Set up Household fund': 'Sediakan dana keluarga',
  'Set up Group fund': 'Sediakan dana kumpulan',
  'Add contribution': 'Tambah sumbangan',
  'Set up Trip money first before adding a contribution.': 'Sediakan wang perjalanan dahulu sebelum menambah sumbangan.',
  'Set up the Household fund first before adding a contribution.': 'Sediakan dana keluarga dahulu sebelum menambah sumbangan.',
  'Set up the Group fund first before adding a contribution.': 'Sediakan dana kumpulan dahulu sebelum menambah sumbangan.',
  'Choose an active money holder before adding a contribution.': 'Pilih pemegang wang yang aktif sebelum menambah sumbangan.',
  'Choose an active money holder before saving.': 'Pilih pemegang wang yang aktif sebelum menyimpan.',
  'No active members are available to hold this money.': 'Tiada ahli aktif yang tersedia untuk memegang wang ini.',
  'Choose a member': 'Pilih ahli',
  'Money collected': 'Wang dikumpul',
  'Money available': 'Wang tersedia',
  'Member contributions': 'Sumbangan ahli',
  'Collected money': 'Wang dikumpul',
  'Who is holding the money?': 'Siapa yang memegang wang?',
  'Who added the money?': 'Siapa yang menambah wang?',
  'This is a shared-money record. It does not move money between bank accounts.': 'Ini ialah rekod wang kongsi. Ia tidak memindahkan wang antara akaun bank.',
  'Paid using collected Household fund': 'Dibayar menggunakan dana keluarga yang dikumpul',
  'Paid using collected Group fund': 'Dibayar menggunakan dana kumpulan yang dikumpul',
  'Offline & sync': 'Luar talian & segerak',
  'Saved on this device': 'Disimpan pada peranti ini',
  'Sync now': 'Segerak sekarang',
  'Syncing…': 'Sedang menyegerak…',
  'You are offline.': 'Anda sedang luar talian.',
  'Saving offline': 'Menyimpan luar talian',
  'Save on this device': 'Simpan pada peranti ini',
  'Nothing waiting to sync': 'Tiada yang menunggu untuk disegerakkan',
  'Waiting': 'Menunggu',
  'Stored only on this device': 'Disimpan pada peranti ini sahaja',
  'Duplicate-safe': 'Dilindungi daripada pendua',
  'Remove saved entry': 'Buang rekod tersimpan',
  'Retry': 'Cuba lagi',
  'Use offline storage only on a private or trusted device.': 'Gunakan simpanan luar talian hanya pada peranti peribadi atau yang dipercayai.',
  'Online still required for other actions.': 'Internet masih diperlukan untuk tindakan lain.',

};

function translateExact(value: string, language: Language): string {
  if (language !== 'ms') return value;
  const match = value.match(/^(\s*)(.*?)(\s*)$/s);
  if (!match) return value;
  const translated = malayPhrases[match[2]];
  return translated ? `${match[1]}${translated}${match[3]}` : value;
}

export function translatePhrase(language: Language, value: string): string {
  return translateExact(value, language);
}

const originalText = new WeakMap<Text, string>();
const translatedAttributes = ['placeholder', 'title', 'aria-label'] as const;

function localizeTextNode(node: Text, language: Language) {
  const parent = node.parentElement;
  if (!parent || parent.closest('script,style,code,pre,textarea,[data-no-auto-translate]')) return;
  if (!originalText.has(node)) originalText.set(node, node.nodeValue || '');
  const original = originalText.get(node) || '';
  const next = translateExact(original, language);
  if (node.nodeValue !== next) node.nodeValue = next;
}

function localizeElement(element: Element, language: Language) {
  translatedAttributes.forEach((attribute) => {
    const current = element.getAttribute(attribute);
    if (current === null) return;
    const originalAttribute = `data-bajetbn-original-${attribute}`;
    if (!element.hasAttribute(originalAttribute)) element.setAttribute(originalAttribute, current);
    const original = element.getAttribute(originalAttribute) || current;
    const next = translateExact(original, language);
    if (current !== next) element.setAttribute(attribute, next);
  });

  element.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) localizeTextNode(child as Text, language);
    else if (child.nodeType === Node.ELEMENT_NODE) localizeElement(child as Element, language);
  });
}

export function localizeDocument(language: Language, root: ParentNode = document) {
  const element = root instanceof Document ? root.documentElement : root instanceof Element ? root : null;
  if (element) localizeElement(element, language);
}
